import {
	computeCaretRect,
	focus,
	isHorizontalEdge,
	isVerticalEdge,
	placeCaretAtHorizontalEdge,
	placeCaretAtVerticalEdge,
	isRTL,
	isFormElement,
} from '@wordpress/dom';
import { UP, DOWN, LEFT, RIGHT } from '@wordpress/keycodes';
import { useDispatch, useSelect } from '@wordpress/data';
import { useRefEffect } from '@wordpress/compose';
import { getBlockType, hasBlockSupport } from '@wordpress/blocks';
import {
	getBlockClientId,
	getSelectionEditableElement,
	isInSameBlock,
} from '../../utils/dom';
import { store as blockEditorStore } from '../../store';
import { setContentEditableWrapper } from './utils';

/**
 * Returns true if the element should consider edge navigation upon a keyboard
 * event of the given directional key code, or false otherwise.
 *
 * @param element     HTML element to test.
 * @param keyCode     KeyboardEvent keyCode to test.
 * @param hasModifier Whether a modifier is pressed.
 *
 * @return Whether element should consider edge navigation.
 */
export function isNavigationCandidate(
	element: Element,
	keyCode: number,
	hasModifier: boolean
): boolean {
	const isVertical = keyCode === UP || keyCode === DOWN;
	const { tagName } = element;
	const elementType = element.getAttribute( 'type' );

	// Native inputs should not navigate vertically, unless they are simple types that don't need up/down arrow keys.
	if ( isVertical && ! hasModifier ) {
		if ( tagName === 'INPUT' ) {
			const verticalInputTypes = [
				'date',
				'datetime-local',
				'month',
				'number',
				'range',
				'time',
				'week',
			];
			// A missing type is not in the list.
			return ! verticalInputTypes.includes( elementType as string );
		}
		return true;
	}

	// Native inputs should not navigate horizontally, unless they are simple types that don't need left/right arrow keys.
	if ( tagName === 'INPUT' ) {
		const simpleInputTypes = [
			'button',
			'checkbox',
			'number',
			'color',
			'file',
			'image',
			'radio',
			'reset',
			'submit',
		];
		return simpleInputTypes.includes( elementType as string );
	}

	// Native textareas should not navigate horizontally.
	return tagName !== 'TEXTAREA';
}

/**
 * Returns the optimal tab target from the given focused element in the desired
 * direction. A preference is made toward text fields, falling back to the block
 * focus stop if no other candidates exist for the block.
 *
 * @param target           Currently focused text field.
 * @param isReverse        True if considering as the first field.
 * @param containerElement Element containing all blocks.
 * @param onlyVertical     Whether to only consider tabbable elements
 *                         that are visually above or under the
 *                         target.
 *
 * @return Optimal tab target, if one exists.
 */
export function getClosestTabbable(
	target: Element,
	isReverse: boolean,
	containerElement: Element,
	onlyVertical?: boolean
): HTMLElement | undefined {
	// Since the current focus target is not guaranteed to be a text field, find
	// all focusables. Tabbability is considered later.
	let focusableNodes = focus.focusable.find( containerElement );

	if ( isReverse ) {
		focusableNodes.reverse();
	}

	// Consider as candidates those focusables after the current target. It's
	// assumed this can only be reached if the target is focusable (on its
	// keydown event), so no need to verify it exists in the set.
	focusableNodes = focusableNodes.slice(
		focusableNodes.indexOf( target as HTMLElement ) + 1
	);

	let targetRect: DOMRect;

	if ( onlyVertical ) {
		targetRect = target.getBoundingClientRect();
	}

	function isTabCandidate( node: HTMLElement ) {
		// Skip if there's only one child that is content editable (and thus a
		// better candidate).
		if (
			node.children.length === 1 &&
			// The node has a single child element.
			isInSameBlock( node, node.firstElementChild! ) &&
			node.firstElementChild!.getAttribute( 'contenteditable' ) === 'true'
		) {
			return;
		}

		// Wrappers that merge with the text flow dissolve into it: their
		// content is the better candidate. Any other container is a
		// boundary the caret stops on.
		// `getBlockType` finds no block type for a missing name.
		const blockType = getBlockType(
			node.getAttribute( 'data-type' ) as string
		);
		if (
			node.contentEditable !== 'true' &&
			getBlockClientId( node ) &&
			blockType &&
			( blockType.merge ||
				hasBlockSupport( blockType, '__experimentalOnMerge' ) ) &&
			focus.focusable
				.find( node )
				// Exclude form elements for now because primary+a cannot be
				// used to select the parent element.
				.filter( ( element ) => ! isFormElement( element ) ).length !==
				0
		) {
			return false;
		}

		// Not a candidate if the node is not tabbable.
		if ( ! focus.tabbable.isTabbableIndex( node ) ) {
			return false;
		}

		// Skip focusable elements such as links within content editable
		// nodes: nodes whose closest editable host is an editable element
		// within a block. When an editable root (e.g. the canvas wrapper)
		// is the editing host, everything within it is content editable,
		// but focusables like block wrappers are not text content.
		if (
			node.isContentEditable &&
			node.contentEditable !== 'true' &&
			getBlockClientId( node.closest( '[contenteditable="true"]' ) )
		) {
			return false;
		}

		if ( onlyVertical ) {
			const nodeRect = node.getBoundingClientRect();

			if (
				nodeRect.left >= targetRect.right ||
				nodeRect.right <= targetRect.left
			) {
				return false;
			}
		}

		return true;
	}

	return focusableNodes.find( isTabCandidate );
}

export default function useArrowNav() {
	const {
		getMultiSelectedBlocksStartClientId,
		getMultiSelectedBlocksEndClientId,
		getNextBlockClientId,
		getPreviousBlockClientId,
		getSelectedBlockClientId,
		getSelectionStart,
		getSettings,
		hasMultiSelection,
		__unstableIsFullySelected,
	} = useSelect( blockEditorStore );
	const { selectBlock, multiSelect } = useDispatch( blockEditorStore );
	return useRefEffect< HTMLElement >( ( node ) => {
		// Here a DOMRect is stored while moving the caret vertically so
		// vertical position of the start position can be restored. This is to
		// recreate browser behaviour across blocks.
		let verticalRect: DOMRect | null | undefined;

		function onMouseDown() {
			verticalRect = null;
		}

		function isClosestTabbableABlock(
			target: Element,
			isReverse: boolean
		) {
			const closestTabbable = getClosestTabbable(
				target,
				isReverse,
				node
			);
			return closestTabbable && getBlockClientId( closestTabbable );
		}

		function onKeyDown( event: KeyboardEvent ) {
			// Abort if navigation has already been handled (e.g. RichText
			// inline boundaries).
			if ( event.defaultPrevented ) {
				return;
			}

			const { keyCode, shiftKey, ctrlKey, altKey, metaKey } = event;
			// When the wrapper is contentEditable and holds focus (the
			// selected block supports `editableRoot`), the event targets the
			// wrapper; resolve the editable element containing the selection.
			const target =
				( event.target === node &&
					( getSelectionEditableElement(
						// A mounted element's window has a selection.
						node.ownerDocument.defaultView!.getSelection()!,
						node
					) as HTMLElement | undefined ) ) ||
				// Key events reach the listener from elements.
				( event.target as HTMLElement );
			const isUp = keyCode === UP;
			const isDown = keyCode === DOWN;
			const isLeft = keyCode === LEFT;
			const isRight = keyCode === RIGHT;
			const isReverse = isUp || isLeft;
			const isHorizontal = isLeft || isRight;
			const isVertical = isUp || isDown;
			const isNav = isHorizontal || isVertical;
			const hasModifier = shiftKey || ctrlKey || altKey || metaKey;
			const isNavEdge = isVertical ? isVerticalEdge : isHorizontalEdge;
			const { ownerDocument } = node;
			const { defaultView } = ownerDocument;

			if ( ! isNav ) {
				return;
			}

			// In preview mode, navigation is handled by useSelectableBlocksNav.
			if ( getSettings().isPreviewMode ) {
				return;
			}

			// If there is a multi-selection, the arrow keys should collapse the
			// selection to the start or end of the selection.
			if ( hasMultiSelection() ) {
				if ( shiftKey ) {
					// A fully selected multi-selection has no native
					// selection to extend (use-multi-selection cleared it),
					// so grow or shrink it by one block at the focus end.
					// Only without a usable native selection: a selection
					// that is fully selected because it resolves to a
					// nesting ancestor keeps its native selection, which
					// the browser extends natively (and the observer
					// promotes to the common level).
					// A mounted element's window has a selection.
					const selection = defaultView!.getSelection()!;
					if (
						__unstableIsFullySelected() &&
						( ! selection.rangeCount || selection.isCollapsed )
					) {
						const anchorClientId =
							getMultiSelectedBlocksStartClientId();
						const focusClientId =
							getMultiSelectedBlocksEndClientId();
						// A multi-selection has a start and an end.
						const nextClientId = isReverse
							? getPreviousBlockClientId( focusClientId! )
							: getNextBlockClientId( focusClientId! );

						if ( nextClientId ) {
							multiSelect( anchorClientId!, nextClientId );
							event.preventDefault();
						}
					}
					return;
				}

				// Only handle if we have a full selection (not a native partial
				// selection).
				if ( ! __unstableIsFullySelected() ) {
					return;
				}

				event.preventDefault();

				// A multi-selection has a start and an end.
				if ( isReverse ) {
					selectBlock( getMultiSelectedBlocksStartClientId()! );
				} else {
					selectBlock( getMultiSelectedBlocksEndClientId()!, -1 );
				}

				return;
			}

			// A block selected without a text selection within it (e.g. an
			// image or spacer) has no native selection to extend: start a
			// block multi-selection with the adjacent block.
			if (
				shiftKey &&
				getSelectedBlockClientId() &&
				! getSelectionStart().attributeKey
			) {
				const selectedClientId = getSelectedBlockClientId();
				// The check above makes sure a block is selected.
				const nextClientId = isReverse
					? getPreviousBlockClientId( selectedClientId! )
					: getNextBlockClientId( selectedClientId! );

				if ( nextClientId ) {
					multiSelect( selectedClientId!, nextClientId );
					event.preventDefault();
				}
				return;
			}

			// Abort if our current target is not a candidate for navigation
			// (e.g. preserve native input behaviors).
			if ( ! isNavigationCandidate( target, keyCode, hasModifier ) ) {
				return;
			}

			// When pressing any key other than up or down, the initial vertical
			// position must ALWAYS be reset. The vertical position is saved so
			// it can be restored as well as possible on subsequent vertical
			// arrow key presses. It may not always be possible to restore the
			// exact same position (such as at an empty line), so it wouldn't be
			// good to compute the position right before any vertical arrow key
			// press.
			if ( ! isVertical ) {
				verticalRect = null;
			} else if ( ! verticalRect ) {
				verticalRect = computeCaretRect( defaultView! );
			}

			// In the case of RTL scripts, right means previous and left means
			// next, which is the exact reverse of LTR.
			const isReverseDir = isRTL( target ) ? ! isReverse : isReverse;
			const { keepCaretInsideBlock } = getSettings();

			if ( shiftKey ) {
				if ( isNavEdge( target, isReverse ) ) {
					if ( isClosestTabbableABlock( target, isReverse ) ) {
						setContentEditableWrapper( node, true );
					} else if ( node.contentEditable === 'true' ) {
						// There is no block to extend the selection into.
						// Within an editable wrapper the selection could
						// natively escape into surrounding editable elements
						// (e.g. the post title), so keep it inside the block
						// by extending it to the block's edge.
						const selection = defaultView!.getSelection()!;
						selection.extend(
							target,
							isReverse ? 0 : target.childNodes.length
						);
						event.preventDefault();
					}
				}
			} else if (
				isVertical &&
				isVerticalEdge( target, isReverse ) &&
				// When Alt is pressed, only intercept if the caret is also at
				// the horizontal edge.
				( altKey ? isHorizontalEdge( target, isReverseDir ) : true ) &&
				! keepCaretInsideBlock
			) {
				const closestTabbable = getClosestTabbable(
					target,
					isReverse,
					node,
					true
				);

				if ( closestTabbable ) {
					placeCaretAtVerticalEdge(
						closestTabbable,
						// When Alt is pressed, place the caret at the furthest
						// horizontal edge and the furthest vertical edge.
						altKey ? ! isReverse : isReverse,
						// The rect is set on the first vertical key press.
						altKey ? undefined : verticalRect!
					);
					event.preventDefault();
				}
			} else if (
				isHorizontal &&
				defaultView!.getSelection()!.isCollapsed &&
				isHorizontalEdge( target, isReverseDir ) &&
				! keepCaretInsideBlock
			) {
				const closestTabbable = getClosestTabbable(
					target,
					isReverseDir,
					node
				);
				// `placeCaretAtHorizontalEdge` ignores a missing element.
				placeCaretAtHorizontalEdge(
					closestTabbable as HTMLElement,
					isReverse
				);
				event.preventDefault();
			}
		}

		node.addEventListener( 'mousedown', onMouseDown );
		node.addEventListener( 'keydown', onKeyDown );
		return () => {
			node.removeEventListener( 'mousedown', onMouseDown );
			node.removeEventListener( 'keydown', onKeyDown );
		};
	}, [] );
}
